import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Controller, get, useFormContext, useWatch } from "react-hook-form";

import { classNames } from "helpers/commonHelpers";
import Radio from "components/v2/Radio/Radio";
import Input from "components/v2/Input/Input";
import NumericInput from "components/v2/NumericInput/NumericInput";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { setSpecialRequirementAdditionalInformationValue } from "helpers/validationHelpers";
import { serviceRequirementRadioTestId, testIdFromFieldName } from "@RequestFormV2/utils/testIds";
import { useService } from "@RequestFormV2/sections/Service/context";
import {
  getServiceRequirementAdditionalInfoError,
  ServiceRequirementDataType,
} from "@RequestFormV2/sections/Service/validation/validateServiceRequirements";
import { getRequiredMessage } from "@RequestFormV2/validation/utils/validationMessages";

type RequirementFieldDef = {
  label: string;
  value: string;
  data_type?: ServiceRequirementDataType;
  additional_information_required?: boolean;
};

const radioOptions = [
  { value: "true", labelId: "general.yes", defaultMessage: "Yes" },
  { value: "false", labelId: "general.no", defaultMessage: "No" },
] as const;

function RequirementField({ requirementField, index }: { requirementField: RequirementFieldDef; index: number }) {
  const intl = useIntl();
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const valueFieldName = `service.special_requirements.${requirementField.value}.value`;
  const additionalInfoFieldName = `service.special_requirements.${requirementField.value}.additional_information`;
  const saveContext = useRequestFormSaveOrNull();

  const selectedValue = useWatch({ control, name: valueFieldName });
  const isYes = selectedValue === "true";
  const valueError = get(errors, valueFieldName);

  useReactiveReviewClear(
    valueFieldName,
    () => selectedValue === "true" || selectedValue === "false",
    [selectedValue],
  );

  const dataType: ServiceRequirementDataType = requirementField.data_type ?? "string";
  const isAdditionalInfoRequired = requirementField.additional_information_required ?? true;

  const registerOptions = useMemo(
    () => ({
      validate: (value: string) => getServiceRequirementAdditionalInfoError(value, dataType, isAdditionalInfoRequired, intl),
      ...(dataType === "integer" || dataType === "double" || dataType === "email"
        ? {
            setValueAs: (value: string) => setSpecialRequirementAdditionalInformationValue(value, dataType),
          }
        : {}),
    }),
    [dataType, isAdditionalInfoRequired, intl],
  );

  const normalizeValue = useMemo(
    () =>
      dataType === "email" ? (value: string) => setSpecialRequirementAdditionalInformationValue(value, dataType) : undefined,
    [dataType],
  );

  const renderAdditionalInformationInput = () => {
    const sharedProps = {
      name: additionalInfoFieldName,
      required: isAdditionalInfoRequired,
      hintText: isAdditionalInfoRequired ? getRequiredMessage(intl) : undefined,
      testId: testIdFromFieldName(additionalInfoFieldName, "input"),
      registerOptions,
      validateOnChange: true,
      disableReviewErrorClear: true,
    };

    if (dataType === "integer" || dataType === "double") {
      return <NumericInput {...sharedProps} dataType={dataType} />;
    }

    return <Input {...sharedProps} type="text" normalizeValue={normalizeValue} />;
  };

  return (
    <div>
      <label className={classNames({ required: true })}>{requirementField.label}</label>
      <Controller
        name={valueFieldName}
        control={control}
        rules={{}}
        render={({ field }) => (
          <Radio
            isInline
            options={radioOptions.map((option) => ({
              value: option.value,
              label: intl.formatMessage({ id: option.labelId, defaultMessage: option.defaultMessage }),
            }))}
            value={field.value ?? "false"}
            size="md"
            onChange={(value) => {
              field.onChange(value);
              if (value === "false") {
                setValue(additionalInfoFieldName, "");
                clearErrors(additionalInfoFieldName);
              }
              saveContext?.saveField(valueFieldName, value).catch(() => undefined);
            }}
            getOptionTestId={(option) => serviceRequirementRadioTestId(index + 1, option.value as "true" | "false")}
          />
        )}
      />
      {valueError?.message && <FieldErrorBadge message={String(valueError.message)} />}
      {isYes && <div className="mt-md">{renderAdditionalInformationInput()}</div>}
    </div>
  );
}

export default function RequirementsSection() {
  const intl = useIntl();

  const { serviceDetailsConfig } = useService();

  const showRequirementsFields = serviceDetailsConfig?.service_options?.request_service_requirement;
  const requirementsFields = serviceDetailsConfig?.service_options?.request_service_requirement_values;

  if (!showRequirementsFields) return null;

  return (
    <div className="rfv2-form-section">
      <FormSectionLabel
        label={intl.formatMessage({ id: "request-form.service.requirements.label", defaultMessage: "Requirements" })}
      />
      <div className="rfv2-form-section__content">
        <div className="d-flex flex-column gap-2xl">
          {requirementsFields?.map((requirementField: RequirementFieldDef, index) => (
            <RequirementField key={requirementField.value} requirementField={requirementField} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}
